import React, { useState } from 'react';
import {
  FileCheck2,
  Calendar,
  Building,
  CheckCircle2,
  Clock,
  Search,
  Plus,
  FileText,
  AlertCircle,
} from 'lucide-react';
import { GovStatutoryFiling } from '../../types';
import { INITIAL_GOV_FILINGS } from '../../data/adminModulesData';

export const AdminGovFilingView: React.FC = () => {
  const [filings, setFilings] = useState<GovStatutoryFiling[]>(INITIAL_GOV_FILINGS);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  // New filing state
  const [newAgency, setNewAgency] = useState<GovStatutoryFiling['agency']>('land_dept');
  const [newAgencyName, setNewAgencyName] = useState('สำนักงานที่ดินจังหวัดพระนครศรีอยุธยา');
  const [newTitle, setNewTitle] = useState('');
  const [newYear, setNewYear] = useState('2569');
  const [newDueDate, setNewDueDate] = useState('');
  const [newRef, setNewRef] = useState('');
  const [newHandler, setNewHandler] = useState('ฝ่ายธุรการการเงินนิติบุคคล');
  const [newNotes, setNewNotes] = useState('');

  const filteredFilings = filings.filter(
    (f) =>
      f.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.agency_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.reference_number.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddFiling = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle) return;

    const newFiling: GovStatutoryFiling = {
      id: `gov-${Date.now()}`,
      agency: newAgency,
      agency_name: newAgencyName,
      title: newTitle,
      filing_year: newYear,
      status: 'in_progress',
      due_date: newDueDate || '2026-12-31',
      reference_number: newRef || `GOV-AY-${Math.floor(1000 + Math.random() * 9000)}`,
      handled_by: newHandler,
      notes: newNotes,
    };

    setFilings([newFiling, ...filings]);
    setShowAddModal(false);
    setNewTitle('');
    setNewNotes('');
  };

  const updateStatus = (id: string, newStatus: GovStatutoryFiling['status']) => {
    setFilings((prev) =>
      prev.map((f) =>
        f.id === id
          ? {
              ...f,
              status: newStatus,
              filing_date: newStatus === 'submitted' || newStatus === 'approved' ? new Date().toISOString().split('T')[0] : f.filing_date,
            }
          : f
      )
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-sky-50 text-sky-600 border border-sky-200">
            <FileCheck2 className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900">
              12. งานยื่นเอกสารและรายงานต่อหน่วยงานราชการ (Government & Statutory Filings)
            </h2>
            <p className="text-xs text-slate-500">
              ติดตามกำหนดเวลายื่นเอกสารสำนักงานที่ดิน, สรรพากร, ประกันสังคม, และส่งงบการเงินผู้สอบบัญชี
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
        >
          <Plus className="w-4 h-4" /> บันทึกการยื่นเอกสารใหม่
        </button>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <span className="text-xs text-slate-500">เอกสารยื่นราชการทั้งหมด</span>
          <p className="text-2xl font-bold text-slate-900 font-mono">{filings.length} รายการ</p>
          <p className="text-[11px] text-slate-400">ครบตามข้อกำหนดกฎหมาย</p>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <span className="text-xs text-slate-500">อนุมัติ/เสร็จสิ้นแล้ว</span>
          <p className="text-2xl font-bold text-emerald-700 font-mono">
            {filings.filter((f) => f.status === 'approved').length} รายการ
          </p>
          <p className="text-[11px] text-emerald-600 font-medium">ได้รับหนังสือสำคัญแล้ว</p>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <span className="text-xs text-slate-500">ยื่นผ่านระบบเรียบร้อย (งวดล่าสุด)</span>
          <p className="text-2xl font-bold text-blue-700 font-mono">
            {filings.filter((f) => f.status === 'submitted').length} รายการ
          </p>
          <p className="text-[11px] text-blue-600 font-medium">เช่น ประกันสังคม & ภาษี ภ.ง.ด.</p>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <span className="text-xs text-slate-500">อยู่ระหว่างดำเนินการ</span>
          <p className="text-2xl font-bold text-amber-600 font-mono">
            {filings.filter((f) => f.status === 'in_progress').length} รายการ
          </p>
          <p className="text-[11px] text-amber-600">เช่น ประสานงานเทศบาล</p>
        </div>
      </div>

      {/* Main Filings List */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex flex-col md:flex-row items-center justify-between gap-3 bg-slate-50/50">
          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="ค้นหาชื่อหน่วยงาน, เลขที่อ้างอิง, หัวข้อ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-slate-200 bg-white"
            />
          </div>
          <span className="text-xs text-slate-500">รายการทั้งหมด {filings.length} ฉบับ</span>
        </div>

        <div className="divide-y divide-slate-100">
          {filteredFilings.map((filing) => (
            <div key={filing.id} className="p-5 hover:bg-slate-50/60 transition-colors space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-sky-100 text-sky-800 border border-sky-200">
                    {filing.agency_name}
                  </span>
                  <span className="text-xs text-slate-400 font-mono">
                    ปีงบประมาณ: {filing.filing_year}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                      filing.status === 'approved'
                        ? 'bg-emerald-100 text-emerald-800'
                        : filing.status === 'submitted'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {filing.status === 'approved' && '✅ อนุมัติ / ได้รับเอกสารแล้ว'}
                    {filing.status === 'submitted' && '📤 ยื่นแบบสำเร็จแล้ว'}
                    {filing.status === 'in_progress' && '⏳ อยู่ระหว่างดำเนินการ'}
                  </span>
                </div>
              </div>

              <div>
                <h4 className="font-bold text-slate-900 text-sm">{filing.title}</h4>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed bg-slate-50 p-3 rounded-xl">
                  {filing.notes}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-xs pt-1 font-mono">
                <div className="text-slate-500 space-x-3">
                  <span>เลขที่อ้างอิง: <strong className="text-slate-800">{filing.reference_number}</strong></span>
                  <span>ผู้รับผิดชอบ: <strong className="text-slate-800">{filing.handled_by}</strong></span>
                  {filing.filing_date && (
                    <span className="text-emerald-700 font-medium">วันที่ยื่น: {filing.filing_date}</span>
                  )}
                </div>

                <select
                  value={filing.status}
                  onChange={(e) => updateStatus(filing.id, e.target.value as any)}
                  className="text-xs p-1.5 rounded-lg border border-slate-200 bg-white font-medium text-slate-700 cursor-pointer font-sans"
                >
                  <option value="in_progress">อยู่ระหว่างดำเนินการ</option>
                  <option value="submitted">ยื่นแบบแล้ว</option>
                  <option value="approved">อนุมัติเรียบร้อย</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Filing Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-100 overflow-hidden">
            <div className="p-5 bg-slate-900 text-white flex items-center justify-between">
              <h3 className="font-bold text-base">บันทึกการยื่นเอกสารให้หน่วยงานราชการ</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-white">✕</button>
            </div>

            <form onSubmit={handleAddFiling} className="p-6 space-y-4 text-xs">
              <div>
                <label className="font-semibold text-slate-700 block mb-1">หน่วยงานราชการ *</label>
                <select
                  value={newAgencyName}
                  onChange={(e) => setNewAgencyName(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                >
                  <option value="สำนักงานที่ดินจังหวัดพระนครศรีอยุธยา">สำนักงานที่ดินจังหวัดพระนครศรีอยุธยา</option>
                  <option value="ผู้สอบบัญชีรับอนุญาต (CPA Thailand) & พัฒนาธุรกิจการค้า">ผู้สอบบัญชีรับอนุญาต (CPA) & พัฒนาธุรกิจการค้า</option>
                  <option value="สำนักงานประกันสังคมจังหวัดพระนครศรีอยุธยา">สำนักงานประกันสังคมจังหวัดพระนครศรีอยุธยา</option>
                  <option value="กรมสรรพากร (พื้นที่อยุธยา)">กรมสรรพากร (พื้นที่อยุธยา)</option>
                  <option value="เทศบาลเมืองอโยธยา / อบต.">เทศบาลเมืองอโยธยา / อบต.</option>
                </select>
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">ชื่อเรื่อง / แบบฟอร์มที่ยื่น *</label>
                <input
                  type="text"
                  required
                  placeholder="เช่น ยื่นแบบ สปส.1-10 นำส่งเงินสมทบ"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ปีงบประมาณ / เดือน</label>
                  <input
                    type="text"
                    value={newYear}
                    onChange={(e) => setNewYear(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">เลขที่หนังสือ / อ้างอิง</label>
                  <input
                    type="text"
                    placeholder="เช่น อย.0024/..."
                    value={newRef}
                    onChange={(e) => setNewRef(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">บันทึกเพิ่มเติม / ผลการดำเนินการ</label>
                <textarea
                  rows={3}
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  placeholder="รายละเอียดเอกสารแนบ หรือผลการอนุมัติ..."
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 font-medium"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-blue-700 hover:bg-blue-800 text-white font-bold"
                >
                  บันทึกข้อมูล
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
